import React, { useEffect, useState } from 'react'

import { useAuthentication } from '@/context/AuthProvider'
import { useBottomSheet } from '@/context/BottomSheetContext'
import { ContextValue } from '@/context/Cities'
import { useDevice } from '@/context/Device'
import { useModal } from '@/context/ModalContext'
import { ToastError } from '@/design-lib/components/Toast/ToastError'
import api from '@/lib/api-gluglu-auth'
import { getCities } from '@/page-commons/citiesQuery'
import { ListingCate } from '@/page-commons/listingBySlug'
import { Listings_Detail } from '@/types/cate-graphql-generated-types'
import { isStorageSupported } from '@/utils/isStorageSupported'
import { handleNewListingsAlertClick, handleSystem } from '@/utils/tracking/eventsHandlers'

import { createPayload } from '../../Form/createPayload'
import { ContentDefault } from '../Common/ContentDefault'
import { ContentLoader, FORCED_LOADING_TIME } from '../Common/ContentLoader'
import { ContentLogin } from '../Common/ContentLogin'

import { ContentSuccess } from './ContentSuccess'

type Props = {
  cities: ContextValue
  listing: ListingCate
  onSaveSuccess: VoidFunction
  similarPropertiesLength: number
  userEmail: string
}

export const getPriceMinCents = (priceCents: number) => {
  return priceCents && Math.ceil((priceCents * 0.9) / 1000000) * 1000000
}

export const getPriceMaxCents = (priceCents: number) => {
  return priceCents && Math.ceil((priceCents * 1.1) / 1000000) * 1000000
}

const SaveSearchDetail: React.FC<Props> = ({ cities, listing, onSaveSuccess, similarPropertiesLength, userEmail }) => {
  const [isSaveSearchSuccessInternalState, setIsSaveSearchSuccessInternalState] = useState<boolean>(false)
  const [isLoadingFavoriteSearch, setIsLoadingFavouriteSearch] = useState(false)
  const [showContentLogin, setShowContentLogin] = useState(false)
  const { isLoading: isLoadingUser, user } = useAuthentication()
  const [error, setError] = useState(false)
  const device = useDevice()

  const { closeModal } = useModal()
  const { closeBottomSheet } = useBottomSheet()

  const handleSubmitUserFavoriteSearch = async () => {
    const favoriteSearchPayload = createPayload({
      listing,
      origin: 'LISTING_CONTACT',
      referenceCitySlug: cities.router.area?.slug,
    })

    try {
      setIsLoadingFavouriteSearch(true)
      setShowContentLogin(false)
      closeBottomSheet()
      await api.submitUserFavoriteSearch(favoriteSearchPayload)
      handleNewListingsAlertClick('ActivateAlert', 'ListingDetail')
      onSaveSuccess()
      setIsSaveSearchSuccessInternalState(true)
    } catch (_e) {
      handleSystem('NewListingsAlert', 'ActivateAlertError')
      setError(true)
    } finally {
      setTimeout(() => {
        setIsLoadingFavouriteSearch(false)
      }, FORCED_LOADING_TIME)
    }
  }

  useEffect(() => {
    isSaveSearchSuccessInternalState && onSaveSuccess()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaveSearchSuccessInternalState])

  useEffect(() => {
    async function run() {
      handleSubmitUserFavoriteSearch()
    }

    if (
      !isLoadingUser &&
      user &&
      isStorageSupported(() => sessionStorage) &&
      sessionStorage.getItem('CASAVO@LISTINGS-SAVE-SEARCH-AFTER-LOGIN')
    ) {
      run().finally(() => {
        sessionStorage.removeItem('CASAVO@LISTINGS-SAVE-SEARCH-AFTER-LOGIN')
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isLoadingUser])

  if (device === 'mobile') {
    return !isSaveSearchSuccessInternalState && !isLoadingFavoriteSearch ? (
      <>
        <ContentDefault
          cities={cities}
          handleSubmitUserFavoriteSearch={handleSubmitUserFavoriteSearch}
          listing={listing}
          origin="LISTING_CONTACT"
          setIsLoadingFavouriteSearch={setIsLoadingFavouriteSearch}
          userEmail={userEmail}
        />
        <ToastError error={error} setError={setError} />
      </>
    ) : isLoadingFavoriteSearch ? (
      <ContentLoader />
    ) : (
      <ContentSuccess similarPropertiesLength={similarPropertiesLength} />
    )
  } else {
    return !isSaveSearchSuccessInternalState && !isLoadingFavoriteSearch && !showContentLogin ? (
      <>
        <ContentDefault
          cities={cities}
          handleSubmitUserFavoriteSearch={handleSubmitUserFavoriteSearch}
          listing={listing}
          origin="LISTING_CONTACT"
          setIsLoadingFavouriteSearch={setIsLoadingFavouriteSearch}
          setShowContentLogin={setShowContentLogin}
          userEmail={userEmail}
        />
        <ToastError error={error} setError={setError} />
      </>
    ) : isLoadingFavoriteSearch ? (
      <ContentLoader />
    ) : showContentLogin ? (
      <ContentLogin
        listing={listing}
        origin="LISTING_CONTACT"
        setIsLoadingFavouriteSearch={setIsLoadingFavouriteSearch}
        userEmail={userEmail}
        onLoggedIn={() => closeModal()}
      />
    ) : (
      <ContentSuccess similarPropertiesLength={similarPropertiesLength} />
    )
  }
}

export default SaveSearchDetail
